/**
 * Main layout for authenticated pages
 */

import { Outlet, Link, useLocation } from 'react-router-dom';
import { useState, useCallback } from 'react';
import {
  Home,
  Trophy,
  Users,
  Calendar,
  BookOpen,
  User,
  Menu,
  X,
  LogOut,
  Settings,
} from 'lucide-react';
import { useAppStore } from '../../stores/appStore';
import { useLogout } from '../../hooks/useAuth';
import { useRouteFocus } from '../../hooks/useRouteFocus';
import { cn } from '../../lib/utils';
import ThemeToggle from '../ui/ThemeToggle';
import UserMenu from './UserMenu';
import SkipToContent from './SkipToContent';

const navigation = [
  { name: 'Início', href: '/inicio', icon: Home },
  { name: 'Jogos', href: '/jogos', icon: Calendar },
  { name: 'Bolões', href: '/boloes', icon: Users },
  { name: 'Regras', href: '/regras', icon: BookOpen },
];

export default function MainLayout() {
  const location = useLocation();
  const user = useAppStore((state) => state.user);
  const logout = useLogout();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useRouteFocus();

  const isActive = (path: string) => location.pathname.startsWith(path);

  // Memoized logout handler for UserMenu
  const handleLogout = useCallback(() => {
    logout.mutate();
  }, [logout]);

  return (
    <div className="min-h-screen bg-neutral-50 dark:bg-neutral-950 transition-colors duration-200">
      <SkipToContent />
      {/* Header */}
      <header className="bg-white dark:bg-neutral-900 border-b border-neutral-200 dark:border-neutral-800 sticky top-0 z-50 transition-colors duration-200">
        <div className="container-wide">
          <div className="flex h-16 items-center justify-between">
            {/* Logo */}
            <Link to="/inicio" className="flex items-center gap-3 group">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary-500 to-primary-600 dark:from-primary-400 dark:to-primary-500 flex items-center justify-center shadow-lg shadow-primary-500/20 group-hover:shadow-primary-500/30 transition-shadow">
                <Trophy className="w-5 h-5 text-white" />
              </div>
              <span className="font-bold text-xl text-neutral-900 dark:text-white hidden sm:block">
                Bolão 2026
              </span>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-1">
              {navigation.map((item) => (
                <Link
                  key={item.href}
                  to={item.href}
                  className={cn(
                    'flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium transition-all duration-200',
                    isActive(item.href)
                      ? 'bg-primary-50 dark:bg-primary-500/10 text-primary-700 dark:text-primary-400'
                      : 'text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800 hover:text-neutral-900 dark:hover:text-white'
                  )}
                >
                  <item.icon className="w-5 h-5" />
                  {item.name}
                </Link>
              ))}
            </nav>

            {/* Right side */}
            <div className="flex items-center gap-2">
              {/* Theme Toggle */}
              <ThemeToggle />

              {/* Desktop user menu - extracted for better perf */}
              <UserMenu user={user} onLogout={handleLogout} />

              {/* Mobile menu button */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className={cn(
                  'md:hidden p-2.5 rounded-xl transition-colors',
                  'text-neutral-600 dark:text-neutral-400',
                  'hover:bg-neutral-100 dark:hover:bg-neutral-800',
                  mobileMenuOpen && 'bg-neutral-100 dark:bg-neutral-800'
                )}
              >
                {mobileMenuOpen ? (
                  <X className="w-6 h-6" />
                ) : (
                  <Menu className="w-6 h-6" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 animate-slide-down">
            <div className="container-wide py-4 space-y-1">
              {navigation.map((item) => (
                <Link
                  key={item.href}
                  to={item.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className={cn(
                    'flex items-center gap-3 px-4 py-3 rounded-xl font-medium transition-colors',
                    isActive(item.href)
                      ? 'bg-primary-50 dark:bg-primary-500/10 text-primary-700 dark:text-primary-400'
                      : 'text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800'
                  )}
                >
                  <item.icon className="w-5 h-5" />
                  {item.name}
                </Link>
              ))}
              <div className="divider my-3" />
              <Link
                to="/perfil"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-3 px-4 py-3 rounded-xl text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800"
              >
                <User className="w-5 h-5" />
                Meu Perfil
              </Link>
              {user?.isAdmin && (
                <Link
                  to="/admin"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800"
                >
                  <Settings className="w-5 h-5" />
                  Administração
                </Link>
              )}
              <button
                onClick={() => {
                  setMobileMenuOpen(false);
                  logout.mutate();
                }}
                className="flex items-center gap-3 w-full px-4 py-3 rounded-xl text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
              >
                <LogOut className="w-5 h-5" />
                Sair
              </button>
            </div>
          </div>
        )}
      </header>

      {/* Main content */}
      <main id="main" tabIndex={-1} className="container-wide py-6 focus:outline-none">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="bg-white dark:bg-neutral-900 border-t border-neutral-200 dark:border-neutral-800 mt-auto transition-colors duration-200">
        <div className="container-wide py-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-sm text-neutral-500 dark:text-neutral-400">
              Bolão 2026 — Copa do Mundo FIFA
            </p>
            <div className="flex items-center gap-4 text-sm text-neutral-500 dark:text-neutral-400">
              <span>Feito com paixão pelo futebol</span>
              <span className="text-neutral-400 dark:text-neutral-500">v2.3.3</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
